import { IllegalArgumentError } from '../error/index.js';
/**
 * Checks that the given value is not null or undefined
 *
 * @param value            the value to check
 * @param objectName       the object name the value belongs to
 * @param propertyName     the property name this value represents
 * @throws IllegalArgumentError if the given value is null or undefined
 * @return the validated value
 */
export function checkDefined(value, objectName, propertyName) {
    if (value == null)
        throw new IllegalArgumentError(`Invalid object [${objectName}]. Missing required property [${propertyName}]`);
    return value;
}
/**
 * Checks that the given numeric values is greater than the given limit
 *
 * @param value            the value to check
 * @param limit            the limit to check against
 * @param objectName       the object name the value belongs to
 * @param propertyName     the property name this value represents
 * @throws IllegalArgumentError if the given value is <= limit
 * @return the validated value
 */
export function checkGreaterThan(value, limit, objectName, propertyName) {
    if (value == null || value <= limit)
        throw new IllegalArgumentError(`Invalid object [${objectName}]; [${propertyName}] must be greater than [${limit}]`);
    return value;
}
/**
 * Checks that the given numeric values is less than the given limit
 *
 * @param value            the value to check
 * @param limit            the limit to check against
 * @param objectName       the object name the value belongs to
 * @param propertyName     the property name this value represents
 * @throws IllegalArgumentError if the given value is >= limit
 * @return the validated value
 */
export function checkLessThan(value, limit, objectName, propertyName) {
    if (value == null || value >= limit)
        throw new IllegalArgumentError(`Invalid object [${objectName}]; [${propertyName}] must be less than [${limit}]`);
    return value;
}
/**
 * Checks that the given value is null or undefined
 *
 * @param value            the value to check
 * @param objectName       the object name the value belongs to
 * @param propertyName     the property name this value represents
 * @throws IllegalArgumentError if the given value is not null or undefined
 * @return the validated value
 */
export function checkNotDefined(value, objectName, propertyName) {
    if (value !== null)
        throw new IllegalArgumentError(`Invalid object [${objectName}]. Not allowed property [${propertyName}]`);
    return value;
}
/**
 * Checks that the array values is not null/undefined and has a valid it least one element
 *
 * @param value            the value to check
 * @param objectName       the object name the value belongs to
 * @param propertyName     the property name this value represents
 * @param minSize          the minimum valid size
 * @throws IllegalArgumentError if the given value is null or undefined
 * @return the validated value
 */
export function checkHasSizeGreaterThan(value, objectName, propertyName, minSize = 1) {
    checkDefined(value, objectName, propertyName);
    const arrayValue = value;
    if (arrayValue.length < minSize)
        throw new IllegalArgumentError(`Invalid object [${objectName} property [${propertyName}], must contain at least [${minSize}] elements`);
    return arrayValue;
}
/**
 * Checks that the given string or array entity property has not exceeding the max allowed length.
 *
 * @param value         The string or array property value
 * @param objectName    The object name
 * @param propertyName  The property name
 * @param maxLength     The max allowed length
 */
export function checkMaxLength(value, objectName, propertyName, maxLength) {
    if (value == null)
        return;
    if (value.length > maxLength) {
        throw new IllegalArgumentError(`Invalid length of object [${objectName}] property [${propertyName}]. Maximum allowed length is [${maxLength}].`);
    }
}
/**
 * Checks that the given string or array entity property has equal ar greater length than specified.
 *
 * @param value         The string or array property value
 * @param objectName    The object name
 * @param propertyName  The property name
 * @param minLength     The min allowed length. By default 1
 */
export function checkMinLength(value, objectName, propertyName, minLength = 1) {
    if (value == null)
        return;
    if (value.length < minLength) {
        throw new IllegalArgumentError(`Invalid length of object [${objectName}] property [${propertyName}]. Minimum allowed length is [${minLength}].`);
    }
}
/**
 * Checks that the given string or array entity property has length in the provided min/max range.
 *
 * @param value         The string or array property value
 * @param objectName    The object name
 * @param propertyName  The property name
 * @param maxLength     The min allowed length
 * @param minLength     The min allowed length. By default 1
 */
export function checkLengthInRange(value, objectName, propertyName, maxLength, minLength = 1) {
    if (value == null)
        return;
    checkMinLength(value, objectName, propertyName, minLength);
    checkMaxLength(value, objectName, propertyName, maxLength);
}
/**
 * Verifies that the provided value is a positive integer or 0 (value >= 0)
 * @param value The value to test
 * @param propertyPath The path to the value on the model
 */
export function checkPositiveInteger(value, propertyPath) {
    if (typeof value !== 'number' || isNaN(value) || value === Infinity || value === -Infinity || value < 0) {
        throw new IllegalArgumentError(`${propertyPath} must be a positive integer, but instead was [${value}]`);
    }
    return value;
}
//# sourceMappingURL=validator.utils.js.map